import { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "../components/icons/Icon";

/**
 * Surface-displaced modal. A NEWER surface for the SAME account
 * (another tab / device) claimed this table's `(table_id, player_id)`
 * singleton slot, so the BE halted THIS surface. This is NOT a
 * logout — the account session, cookie, and IndexedDB row are all
 * intact — so the copy is reassuring + offers a real choice rather
 * than a logout countdown:
 *
 *   - Blue informational tint + 🔄 icon (same severity as
 *     `DisplacedModal`, reusing its `.displaced-modal*` classes) —
 *     the displacement is a single-active-surface convention, not an
 *     error.
 *   - "다른 곳에서 진행 중" body, switching on `seated` for the
 *     in-hand reassurance cue (the money is safe; the other surface
 *     is driving the hand).
 *   - Take-over CTA (primary) re-claims the slot for THIS surface
 *     (`takeOverSurface` reopens the socket); dismiss CTA (secondary)
 *     just closes the modal because the user chose to keep going in
 *     the other surface.
 *
 * Unlike `DisplacedModal` there is NO auto countdown — neither choice
 * is destructive, so the modal dwells until the user picks one. The
 * mount gate is the `pendingSurfaceNotice` latch (App-level), an
 * independent one-way latch the connection / auth teardown never
 * touches.
 */

export interface SurfaceDisplacedModalProps {
  /** True when the caller was seated at a table when the displace
   *  landed. Drives the reassurance copy variant. */
  readonly seated: boolean;
  /** Take-over CTA — re-claim the table slot for this surface
   *  (reopens the WS). The App-root wiring routes this to
   *  `takeOverSurface()`, which also clears the notice latch. */
  readonly onTakeOver: () => void;
  /** Dismiss CTA — the user keeps going in the other surface.
   *  Clears the `pendingSurfaceNotice` latch so the modal unmounts;
   *  this surface stays halted. */
  readonly onDismiss: () => void;
}

export function SurfaceDisplacedModal(
  props: SurfaceDisplacedModalProps,
): JSX.Element | null {
  const { t } = useTranslation();
  const takeOverRef = useRef<HTMLButtonElement | null>(null);
  const firedRef = useRef<boolean>(false);

  const fireTakeOver = useMemo(
    () => () => {
      if (firedRef.current) return;
      firedRef.current = true;
      props.onTakeOver();
    },
    [props],
  );
  const fireDismiss = useMemo(
    () => () => {
      if (firedRef.current) return;
      firedRef.current = true;
      props.onDismiss();
    },
    [props],
  );

  useEffect(() => {
    takeOverRef.current?.focus();
  }, []);

  if (typeof document === "undefined") return null;

  const bodyKey = props.seated
    ? "app.surface_displaced_body_seated"
    : "app.surface_displaced_body";

  return createPortal(
    <div
      className="system-modal surface-displaced-modal"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="surface-displaced-modal-title"
      aria-describedby="surface-displaced-modal-body"
    >
      <div className="system-modal__card">
        <div className="system-modal__icon" aria-hidden="true">
          <Icon name="refresh" size={28} />
        </div>
        <h2 id="surface-displaced-modal-title" className="system-modal__title">
          {t("app.surface_displaced_title")}
        </h2>
        <p
          id="surface-displaced-modal-body"
          className="system-modal__body"
          data-testid="surface-displaced-modal-body"
        >
          {t(bodyKey)}
        </p>
        <div className="system-modal__cta-row">
          <button
            ref={takeOverRef}
            type="button"
            className="btn system-modal__cta"
            onClick={fireTakeOver}
            data-testid="surface-displaced-modal-takeover"
          >
            {t("app.surface_displaced_takeover_cta")}
          </button>
          <button
            type="button"
            className="btn system-modal__cta system-modal__cta--secondary"
            onClick={fireDismiss}
            data-testid="surface-displaced-modal-dismiss"
          >
            {t("app.surface_displaced_dismiss_cta")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
